import React, { useState } from "react";
import { Plus, Search, Trash2 } from "lucide-react";
import {
  useGameTables,
  useCreateGameTable,
  useUpdateGameTable,
  useDeleteGameTable,
} from "../hooks/useGameTables";
import { useGames } from "../hooks/useGames";
import {
  CreateGameTableRequest,
  GameTable,
  UpdateGameTableRequest,
} from "../api/services/gameTables/type";
import { GameTableForm } from "../components/gameTables/GameTableForm";
import { GameTableTable } from "../components/gameTables/GameTableTable";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { Pagination } from "../components/common/Pagination";
import toast from "react-hot-toast";

export const GameTables: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingTable, setEditingTable] = useState<GameTable | undefined>(
    undefined,
  );
  const itemsPerPage = 25;

  const { data, isLoading, isError, error, refetch } = useGameTables({
    limit: 1000,
  });

  const { data: gamesData } = useGames({ limit: 100 });
  const games = gamesData?.games || [];

  const createGameTable = useCreateGameTable();
  const updateGameTable = useUpdateGameTable();
  const deleteGameTable = useDeleteGameTable();

  const filteredGameTables = (data?.gameTables || []).filter((table) => {
    if (!searchTerm) return true;
    return table.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const totalFiltered = filteredGameTables.length;
  const totalPages = Math.ceil(totalFiltered / itemsPerPage);

  const paginatedGameTables = filteredGameTables.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handlePageChange = (page: number) => setCurrentPage(page);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleCreateTable = () => {
    setEditingTable(undefined);
    setShowForm(true);
  };

  const handleEditTable = (table: GameTable) => {
    setEditingTable(table);
    setShowForm(true);
  };

  const handleFormSubmit = (
    formData: CreateGameTableRequest | UpdateGameTableRequest,
  ) => {
    const payload = {
      ...formData,
      min_amount: Number(formData.min_amount),
      max_amount: Number(formData.max_amount),
      limit_number: Number(formData.limit_number),
    };
    if (editingTable) {
      updateGameTable.mutate(
        { id: editingTable.id, data: payload },
        {
          onSuccess: () => {
            toast.success("Game table updated successfully!");
            setShowForm(false);
            setEditingTable(undefined);
          },
          onError: (error: unknown) => {
            toast.error(
              `Failed to update game table: ${(error as Error).message}`,
            );
          },
        },
      );
    } else {
      createGameTable.mutate(payload, {
        onSuccess: () => {
          toast.success("Game table created successfully!");
          setShowForm(false);
        },
        onError: (error: unknown) => {
          toast.error(
            `Failed to create game table: ${(error as Error).message}`,
          );
        },
      });
    }
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tableToDelete, setTableToDelete] = useState<string | null>(null);

  const handleDeleteTable = (id: string) => {
    setTableToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (tableToDelete) {
      deleteGameTable.mutate(tableToDelete, {
        onSuccess: () => {
          toast.success("Game table deleted successfully!");
          setDeleteDialogOpen(false);
          setTableToDelete(null);
        },
        onError: (error: unknown) => {
          toast.error(
            `Failed to delete game table: ${(error as Error).message}`,
          );
        },
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setTableToDelete(null);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingTable(undefined);
  };

  if (isError) {
    return (
      <div className="min-h-screen bg-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-800/20 border border-red-600 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-red-400 mb-2">
              Error Loading Game Tables
            </h3>
            <p className="text-red-300">
              {error?.message ||
                "Failed to load game tables. Please try again."}
            </p>
            <button
              onClick={() => refetch()}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Search and Add Button */}
        <div className="bg-slate-800 rounded-lg p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                size={20}
              />
              <input
                type="text"
                placeholder="Search game tables by name..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={handleCreateTable}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors font-medium whitespace-nowrap"
            >
              <Plus size={20} />
              Add Game Table
            </button>
          </div>
        </div>

        {/* Game Tables Table */}
        <div className="mb-6">
          <GameTableTable
            gameTables={paginatedGameTables}
            onEdit={handleEditTable}
            onDelete={handleDeleteTable}
            isLoading={isLoading}
          />
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}

        {/* Game Table Form Modal */}
        {showForm && (
          <GameTableForm
            table={editingTable}
            isLoading={createGameTable.isPending || updateGameTable.isPending}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
            games={games}
          />
        )}

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteDialogOpen}
          title="Delete Game Table"
          description="Are you sure you want to delete this game table? This action cannot be undone."
          icon={<Trash2 className="h-8 w-8 text-white" />}
          iconBgColor="bg-red-600"
          confirmColor="bg-red-600 hover:bg-red-700"
          confirmText="Delete"
          cancelText="Cancel"
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        />
      </div>
    </div>
  );
};
